import React, { useState, useMemo } from 'react';
import Header from '../components/layout/Header';
import ErrorBanner from '../components/ui/ErrorBanner';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import KPICard from '../components/ui/KPICard';
import { useOpportunities } from '../hooks/useOpportunities';
import { formatCurrency, formatDate, truncate } from '../utils/formatters';
import DateRangePicker from '../components/ui/DateRangePicker';

const STATUS_COLORS = { open: '#22c55e', won: '#6366f1', lost: '#ef4444', abandoned: '#94a3b8' };
const STAGE_COLORS = ['#6366f1','#3b82f6','#0ea5e9','#06b6d4','#10b981','#f59e0b','#f97316','#ef4444','#8b5cf6','#ec4899'];

function inRange(dateStr, start, end) {
  if (!dateStr) return false;
  const d = dateStr.slice(0, 10);
  return d >= start && d <= end;
}

// Inline bar chart component â€” always shows all stages; count is period-filtered
function StageBarChart({ stages, colorOffset }) {
  const maxCount = Math.max(1, ...stages.map(s => s.count));
  const hasAny = stages.some(s => s.count > 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
      {stages.map((stage, si) => {
        const color = STAGE_COLORS[(colorOffset + si) % STAGE_COLORS.length];
        const pct = hasAny ? Math.max(2, (stage.count / maxCount) * 100) : 2;
        return (
          <div key={stage.id || stage.name} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ fontSize: '0.7rem', color: '#64748b', width: '120px', flexShrink: 0, textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {stage.name}
            </div>
            <div style={{ flex: 1, background: '#f1f5f9', borderRadius: '999px', height: '14px', overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: stage.count > 0 ? color : '#e2e8f0', borderRadius: '999px', transition: 'width 0.4s', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '4px' }}>
                {stage.count > 0 && <span style={{ fontSize: '0.6rem', color: '#fff', fontWeight: 700 }}>{stage.count}</span>}
              </div>
            </div>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: stage.count > 0 ? '#1e293b' : '#94a3b8', minWidth: '20px', textAlign: 'right' }}>{stage.count}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function Pipeline() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { data, pipelines, loading, error } = useOpportunities('', '');
  const [selectedPipelineId, setSelectedPipelineId] = useState(null);
  const [selectedStageId, setSelectedStageId] = useState(null);

  
  // Build pipeline data:
  //   count/value = ALL open opps (no date filter)
  //   stages       = ALL stages, each with period-filtered counts
  const pipelineData = useMemo(() => {
    return pipelines.map((p, pi) => {

      // All open opps â€” drives card total (case-insensitive status check)
      const allOpenOpps = data.filter(o => o.pipelineId === p.id && !['won','lost','abandoned'].includes((o.status || '').toLowerCase()));

      //   - Regular pipelines â†’ all open opps, no date filter (so bars always match the total)
      const filteredOpps = allOpenOpps; // all open opps in their current stage

      // Build stage list â€” use pipeline definition; fall back to stages from actual opps
      let stageDefs = p.stages && p.stages.length > 0 ? p.stages : null;
      if (!stageDefs) {
        // Derive unique stages from all open opps in this pipeline
        const seen = new Map();
        allOpenOpps.forEach(o => {
          const id = o.pipelineStageId || o.pipelineStageName;
          if (id && !seen.has(id)) seen.set(id, { id: o.pipelineStageId, name: o.pipelineStageName || o.pipelineStageId });
        });
        stageDefs = [...seen.values()];
      }

      const stages = stageDefs.map(s => {
        const stageOpps = filteredOpps.filter(o => o.pipelineStageId === s.id || o.pipelineStageName === s.name);
        const allStageOpps = allOpenOpps.filter(o => o.pipelineStageId === s.id || o.pipelineStageName === s.name);
        return {
          ...s,
          opps: stageOpps,
          count: stageOpps.length,       // period count â€” for bar chart
          totalCount: allStageOpps.length, // all-time count â€” shown as label
          value: stageOpps.reduce((sum, o) => sum + (o.monetaryValue || o.value || o.amount || 0), 0),
        };
      });

      return {
        ...p,
        opps: filteredOpps,
        stages,
        count: allOpenOpps.length,    // total open â€” always unfiltered
        value: allOpenOpps.reduce((sum, o) => sum + (o.monetaryValue || o.value || o.amount || 0), 0),
        colorOffset: pi,
      };
    });
  }, [pipelines, data]);

  const selectedPipeline = selectedPipelineId ? pipelineData.find(p => p.id === selectedPipelineId) : null;

  const detailOpps = useMemo(() => {
    if (!selectedPipeline) return [];
    if (selectedStageId) return selectedPipeline.stages.find(s => s.id === selectedStageId)?.opps || [];
    return selectedPipeline.opps;
  }, [selectedPipeline, selectedStageId]);

  const openOpps   = useMemo(() => data.filter(o => !['won','lost','abandoned'].includes((o.status || '').toLowerCase())), [data]);
  const totalOpps  = openOpps.length;
  const totalValue = openOpps.reduce((s, o) => s + (o.monetaryValue || o.value || o.amount || 0), 0);

  return (
    <div>
      <Header title="Pipeline" />
      <div style={styles.content}>
        <ErrorBanner message={error} />

        <DateRangePicker onChange={({ start, end }) => { setStartDate(start); setEndDate(end); }} defaultPresetIdx={2} />

        <div style={styles.kpiRow}>
          <KPICard title="Open Opportunities" value={totalOpps} icon="ðŸ“‚" color="#22c55e" loading={loading} />
          <KPICard title="Pipeline Value" value={formatCurrency(totalValue)} icon="ðŸ’°" color="#f59e0b" loading={loading} />
        </div>

        {loading ? <LoadingSpinner /> : (
          <>
            {!selectedPipelineId && (
              <div style={styles.grid}>
                {pipelineData.filter(p => p.count > 0).map((pipeline) => (
                  <div
                    key={pipeline.id}
                    style={styles.pipelineCard}
                    onClick={() => setSelectedPipelineId(pipeline.id)}
                  >
                    {/* Card header */}
                    <div style={{ ...styles.pipelineHeader, borderColor: STAGE_COLORS[pipeline.colorOffset % STAGE_COLORS.length] }}>
                      <div>
                        <div style={styles.pipelineName}>{pipeline.name}</div>
                      </div>
                      <div style={styles.pipelineTotalBadge}>
                        <span style={{ fontSize: '1.5rem', fontWeight: 800, color: STAGE_COLORS[pipeline.colorOffset % STAGE_COLORS.length] }}>{pipeline.count}</span>
                        <span style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: '0.1rem' }}>total</span>
                      </div>
                    </div>

                    {/* Bar chart by stage â€” all stages, counts reflect selected period */}
                    <div style={{ marginTop: '0.75rem' }}>
                      {pipeline.stages.length > 0
                        ? <StageBarChart stages={pipeline.stages} colorOffset={pipeline.colorOffset} />
                        : <div style={{ color: '#94a3b8', fontSize: '0.8rem', textAlign: 'center', padding: '0.5rem 0' }}>No stages defined</div>
                      }
                    </div>

                    {pipeline.value > 0 && (
                      <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: '#64748b', borderTop: '1px solid #f1f5f9', paddingTop: '0.5rem' }}>
                        Total value: <strong style={{ color: '#1e293b' }}>{formatCurrency(pipeline.value)}</strong>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {selectedPipeline && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <button onClick={() => { setSelectedPipelineId(null); setSelectedStageId(null); }} style={styles.backBtn}>
                    â† All Pipelines
                  </button>
                  <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#1e293b' }}>{selectedPipeline.name}</h2>
                  {selectedStageId && (
                    <>
                      <span style={{ color: '#94a3b8' }}>â€º</span>
                      <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#6366f1' }}>
                        {selectedPipeline.stages.find(s => s.id === selectedStageId)?.name}
                      </span>
                      <button onClick={() => setSelectedStageId(null)} style={styles.backBtn}>Clear</button>
                    </>
                  )}
                </div>

                {/* Stage cards */}
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                  {selectedPipeline.stages.map((stage, si) => (
                    <div
                      key={stage.id}
                      onClick={() => setSelectedStageId(selectedStageId === stage.id ? null : stage.id)}
                      style={{
                        ...styles.stageCard,
                        borderColor: selectedStageId === stage.id ? STAGE_COLORS[si % STAGE_COLORS.length] : '#e2e8f0',
                        background: selectedStageId === stage.id ? STAGE_COLORS[si % STAGE_COLORS.length] + '12' : '#fff',
                      }}
                    >
                      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: STAGE_COLORS[si % STAGE_COLORS.length] }}>{stage.count}</div>
                      <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569' }}>{stage.name}</div>
                      {stage.value > 0 && <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: '0.1rem' }}>{formatCurrency(stage.value)}</div>}
                    </div>
                  ))}
                </div>

                <div style={styles.tableCard}>
                  <h3 style={{ ...styles.sectionTitle, marginBottom: '0.75rem' }}>
                    {selectedStageId
                      ? `${selectedPipeline.stages.find(s => s.id === selectedStageId)?.name} â€” ${detailOpps.length} opportunities`
                      : `All Opportunities â€” ${detailOpps.length}`}
                  </h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>Name</th>
                          <th style={styles.th}>Contact</th>
                          <th style={styles.th}>Stage</th>
                          <th style={styles.th}>Value</th>
                          <th style={styles.th}>Status</th>
                          <th style={styles.th}>Last Updated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailOpps.length === 0 ? (
                          <tr><td colSpan={6} style={styles.empty}>No opportunities</td></tr>
                        ) : detailOpps.map((opp, i) => (
                          <tr key={opp.id || i} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                            <td style={styles.td}>{truncate(opp.name || opp.contact?.name || '', 30) || 'â€”'}</td>
                            <td style={styles.td}>{truncate(opp.contact?.name || '', 22) || 'â€”'}</td>
                            <td style={styles.td}>{opp.pipelineStageName || 'â€”'}</td>
                            <td style={styles.td}>{formatCurrency(opp.monetaryValue || opp.value || opp.amount || 0)}</td>
                            <td style={styles.td}>
                              <span style={{ ...styles.statusBadge, background: (STATUS_COLORS[opp.status] || '#94a3b8') + '20', color: STATUS_COLORS[opp.status] || '#94a3b8' }}>
                                {opp.status || 'â€”'}
                              </span>
                            </td>
                            <td style={styles.td}>{formatDate(opp.lastStatusChangeAt || opp.updatedAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  content: { padding: '1.5rem 1.75rem', display: 'flex', flexDirection: 'column', gap: '1rem' },
  kpiRow: { display: 'flex', gap: '1rem', flexWrap: 'wrap' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '1rem' },
  pipelineCard: {
    background: '#fff', borderRadius: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    padding: '1.25rem', cursor: 'pointer', transition: 'box-shadow 0.15s',
  },
  pipelineHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    borderLeft: '4px solid', paddingLeft: '0.75rem',
  },
  pipelineName: { fontSize: '0.95rem', fontWeight: 700, color: '#1e293b' },
  pipelineTotalBadge: { display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.1 },
  stageCard: {
    border: '2px solid', borderRadius: '0.5rem', padding: '0.75rem 1rem',
    cursor: 'pointer', transition: 'all 0.15s', textAlign: 'center', minWidth: '100px',
  },
  backBtn: {
    padding: '0.3rem 0.75rem', background: '#f1f5f9', border: '1px solid #e2e8f0',
    borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, color: '#475569',
  },
  tableCard: { background: '#fff', borderRadius: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '1.25rem 1.5rem' },
  sectionTitle: { margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#1e293b' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' },
  th: { padding: '0.625rem 0.75rem', background: '#f8fafc', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'left', borderBottom: '1px solid #e2e8f0' },
  td: { padding: '0.625rem 0.75rem', borderBottom: '1px solid #f1f5f9', color: '#1e293b', verticalAlign: 'middle' },
  statusBadge: { padding: '0.2rem 0.625rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600 },
  empty: { padding: '2rem', textAlign: 'center', color: '#94a3b8' },
};
